<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center px-4"
        role="dialog"
        aria-modal="true"
        aria-label="Call from mobile app"
      >
        <!-- Scrim -->
        <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />

        <!-- Dialog card -->
        <div class="relative w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-xl">
          <!-- Phone icon -->
          <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
            <svg class="h-7 w-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>

          <h2 class="mb-2 text-center text-base font-semibold text-gray-900 dark:text-slate-100">Call from mobile app</h2>

          <!-- Mobile browser: show deep-link CTA -->
          <template v-if="isMobileBrowser">
            <p class="mb-5 text-center text-sm text-gray-500 dark:text-slate-400">
              Tap the button below to open the Beesphone app and call
              <strong class="text-gray-800 dark:text-slate-200">{{ numberToCall }}</strong>.
            </p>
            <div class="flex flex-col gap-2">
              <a
                :href="deepLinkUrl"
                class="block w-full rounded-xl bg-primary py-3 text-center text-sm font-semibold text-white transition hover:opacity-90"
                @click="handleDeepLinkClick"
              >
                Open Beesphone app
              </a>
              <a
                v-if="storeUrl"
                :href="storeUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="block w-full rounded-xl border border-gray-200 dark:border-slate-700 py-3 text-center text-sm font-medium text-gray-600 dark:text-slate-400 transition hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                {{ isIos ? 'Get on the App Store' : 'Get on Google Play' }}
              </a>
            </div>
          </template>

          <!-- Desktop: inform that calling requires the mobile app -->
          <template v-else>
            <p class="mb-5 text-center text-sm text-gray-500 dark:text-slate-400">
              Calling is only available on the <strong class="text-gray-800 dark:text-slate-200">Beesphone mobile app</strong>.
              Download the app to make calls.
            </p>
            <div class="flex flex-col gap-2">
              <a
                href="https://apps.apple.com/app/beesphone/id6446038298"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-slate-700 py-3 text-sm font-medium text-gray-700 dark:text-slate-300 transition hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                <!-- Apple logo (MDI apple) -->
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.78,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                </svg>
                App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.beesphone.mobile"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-slate-700 py-3 text-sm font-medium text-gray-700 dark:text-slate-300 transition hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                <!-- Google Play logo (MDI google-play) -->
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                Google Play
              </a>
            </div>
          </template>

          <!-- Dismiss -->
          <button
            class="mt-4 w-full rounded-xl py-2 text-sm font-medium text-gray-400 transition hover:text-gray-600 dark:hover:text-slate-300"
            @click="$emit('close')"
          >
            Dismiss
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { CallRecord } from '~/types/calls';
import { isCallInbound } from '~/utils/calls';

const props = defineProps<{
  show: boolean;
  call?: CallRecord | null;
  phoneNumber?: string;
}>();

defineEmits<{ close: [] }>();

// Detect mobile browser platform from user-agent
const userAgent = import.meta.client ? navigator.userAgent : '';
const isIos = /iphone|ipad|ipod/i.test(userAgent);
const isAndroid = /android/i.test(userAgent);
const isMobileBrowser = isIos || isAndroid;

// The number to call — same logic as expandable card display
const numberToCall = computed(() => {
  if (props.phoneNumber) return props.phoneNumber;
  const call = props.call;
  if (!call) return '';
  return isCallInbound(call)
    ? call.callerId || call.callerNumber || ''
    : call.destinationNumber || '';
});

// Deep link opens the app directly to a call intent
// The beesphone:// scheme is registered on both iOS/Android by the native app
const deepLinkUrl = computed(() => `beesphone://call?number=${encodeURIComponent(numberToCall.value)}`);

// Store fallback — shown in the mobile browser if deep link doesn't open the app
const storeUrl = computed(() => {
  if (isIos) return 'https://apps.apple.com/app/beesphone/id6446038298';
  if (isAndroid) return 'https://play.google.com/store/apps/details?id=com.beesphone.mobile';
  return null;
});

// Whether we've already attempted the deep link (used to show the "install" suggestion)
const deepLinkAttempted = ref(false);

function handleDeepLinkClick() {
  deepLinkAttempted.value = true;
  // After navigation attempt, wait briefly; if the page is still visible the app likely isn't installed
  setTimeout(() => {
    if (deepLinkAttempted.value && document.visibilityState !== 'hidden' && storeUrl.value) {
      window.open(storeUrl.value, '_blank', 'noopener,noreferrer');
    }
  }, 2500);
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
